/**
 * Barcodes and product data (docs/18-vorratsbuch.md, Strichcodes; F30): a scanned code checked
 * and written as Open Food Facts keeps it, the product data of Open Food Facts (or its sisters for
 * beauty and other products) as the fields of a new pantry article. Nothing here asks the net;
 * the server does (packages/server/src/barcode.ts).
 */
import { AISLES, DRINKS_AISLE, HOUSEHOLD_AISLES, baseProduct, suggestedKeep, suggestedUnit } from "./base-products.ts";
import type { BaseProduct } from "./base-products.ts";
import { findProduct, lookup, productForCategories } from "@household-kit/food";
import { ALLERGEN_NAMES } from "./drinks.ts";
import { FOOD_PROFILE, HOUSEHOLD_PROFILE, pantryName, suggestedCountBy } from "./pantry.ts";

/** Where a product's data comes from: Open Food Facts and its sisters (all under the ODbL). */
export type ProductSource = "openfoodfacts" | "openbeautyfacts" | "openproductsfacts";

export const SOURCE_NAMES: Readonly<Record<ProductSource, string>> = {
  openfoodfacts: "Open Food Facts",
  openbeautyfacts: "Open Beauty Facts",
  openproductsfacts: "Open Products Facts",
};

/** A product as the server answers for a barcode. */
export interface ProductInfo {
  code: string;
  name: string;
  brand?: string;
  /** As Open Food Facts writes it: „400 g“, „6 x 250 ml“. */
  quantity?: string;
  /** Category tags, most general first: „en:plant-based-foods“, „de:passierte-tomaten“. */
  categories: string[];
  /** Label tags: „en:organic“, „en:eu-organic“. */
  labels: string[];
  /** Ids of the 14 allergens (food's): „milk“, „gluten“. */
  allergens: string[];
  source: ProductSource;
}

/** Open Food Facts' words for the 14 allergens where food's differ: „en:soybeans“ is soy. */
const ALLERGEN_TAGS: Readonly<Record<string, string>> = {
  soybeans: "soy",
  "sesame-seeds": "sesame",
  "sulphur-dioxide-and-sulphites": "sulphites",
  lupin: "lupins",
};

/** The ids of the 14 allergens in Open Food Facts' allergen tags („en:milk,en:soybeans“); others are left out. */
export function allergensOfTags(tags: readonly string[] | string): string[] {
  const list = typeof tags === "string" ? tags.split(",") : tags;
  const ids = list.map((t) => t.trim().replace(/^en:/, "")).map((t) => ALLERGEN_TAGS[t] ?? t);
  return [...new Set(ids.filter((id) => ALLERGEN_NAMES[id] !== undefined))];
}

/** The check digit of a GTIN is right. */
function checkDigitOk(digits: string): boolean {
  let sum = 0;
  for (let i = 0; i < digits.length - 1; i++) {
    const d = Number(digits[digits.length - 2 - i]);
    sum += i % 2 === 0 ? d * 3 : d;
  }
  return (10 - (sum % 10)) % 10 === Number(digits[digits.length - 1]);
}

/**
 * A scanned or typed code as Open Food Facts keeps it, or null if it is no GTIN: only digits,
 * 8, 12, 13 or 14 long with the right check digit. A UPC-A (12) gets a leading 0, a GTIN-14 of a
 * single item (leading 0) loses it.
 */
export function normalizeBarcode(raw: string): string | null {
  const digits = raw.replace(/[\s-]/g, "");
  if (!/^\d+$/.test(digits) || ![8, 12, 13, 14].includes(digits.length) || !checkDigitOk(digits)) return null;
  if (digits.length === 12) return `0${digits}`;
  if (digits.length === 14 && digits.startsWith("0")) return digits.slice(1);
  return digits;
}

/**
 * A code that names no product anywhere else: the shop's scales and own codes (GTIN-13 beginning
 * with 2, GTIN-8 with 0 or 2, the UPC range 02): weight or price is in the code. Not looked up.
 */
export function isStoreBarcode(code: string): boolean {
  if (code.length === 13) return code.startsWith("2") || code.startsWith("02") || code.startsWith("04");
  if (code.length === 8) return code.startsWith("0") || code.startsWith("2");
  return false;
}

const UNIT_FACTORS: Record<string, { unit: "g" | "ml" | "Stk."; factor: number }> = {
  g: { unit: "g", factor: 1 },
  gr: { unit: "g", factor: 1 },
  gramm: { unit: "g", factor: 1 },
  kg: { unit: "g", factor: 1000 },
  mg: { unit: "g", factor: 0.001 },
  ml: { unit: "ml", factor: 1 },
  cl: { unit: "ml", factor: 10 },
  dl: { unit: "ml", factor: 100 },
  l: { unit: "ml", factor: 1000 },
  liter: { unit: "ml", factor: 1000 },
  stk: { unit: "Stk.", factor: 1 },
  "stk.": { unit: "Stk.", factor: 1 },
  stück: { unit: "Stk.", factor: 1 },
  stueck: { unit: "Stk.", factor: 1 },
  st: { unit: "Stk.", factor: 1 },
  pcs: { unit: "Stk.", factor: 1 },
  pieces: { unit: "Stk.", factor: 1 },
  tabs: { unit: "Stk.", factor: 1 },
  rollen: { unit: "Stk.", factor: 1 },
  beutel: { unit: "Stk.", factor: 1 },
};

/**
 * The content of one pack from Open Food Facts' quantity: „400 g“ → 400 g, „1,5 kg“ → 1500 g,
 * „75 cl“ → 750 ml, „6 x 250 ml“ → 1500 ml, „10 Stück“ → 10 Stk.; null if it says nothing clear.
 * Only the first quantity counts („500 g (2 x 250 g)“ is 500 g).
 */
export function parseQuantity(text: string | undefined): { size: number; unit: "g" | "ml" | "Stk." } | null {
  if (!text) return null;
  const t = text.toLowerCase().replace(/(\d),(\d)/g, "$1.$2");
  const m = t.match(/^\s*(?:(\d+)\s*[x×*]\s*)?(\d+(?:\.\d+)?)\s*([a-zäöüß.]+)/);
  if (!m) return null;
  const unit = UNIT_FACTORS[m[3]!.replace(/\.$/, "")];
  if (!unit) return null;
  const size = Math.round(Number(m[2]) * unit.factor * (m[1] ? Number(m[1]) : 1));
  return size > 0 ? { size, unit: unit.unit } : null;
}

/** Open Food Facts' category tags as words the catalogue may know: „de:passierte-tomaten“ → „passierte tomaten“. Most specific first. */
const categoryWords = (tags: readonly string[]) =>
  [...tags]
    .reverse()
    .filter((t) => /^(de|en):/.test(t))
    .map((t) => t.slice(3).replace(/-/g, " "));

/**
 * The base product of a product, in this order:
 * 1. the name, when it is exactly a name of the catalogue („Nutella“, „Rote Zwiebeln“): Open Food
 *    Facts' tags can be wrong (Nutella is tagged cocoa powder);
 * 2. household-kit's fixed table of Open Food Facts categories (H17, `productForCategories`, most
 *    specific tag first), as names in French or English mislead the guess („100% mie Complet“ is
 *    toast, not cornflakes);
 * 3. the guess from the name, then the categories as words, most specific first.
 */
export function baseOfProduct(p: Pick<ProductInfo, "name" | "categories">): BaseProduct | undefined {
  // Drinks too: VorratsBuch keeps juice and oat milk (Martin, 05.11.2026).
  const exact = findProduct(p.name);
  if (exact) return exact;
  const mapped = productForCategories(p.categories);
  if (mapped) return mapped;
  // food's lookup with drinks (guessBaseProduct leaves them out for invoices, which send drinks to KellerLog).
  for (const text of [p.name, ...categoryWords(p.categories)]) {
    const hit = lookup(text)?.product;
    if (hit) return baseProduct(hit.id);
  }
  return undefined;
}

/** A drink by its categories: VorratsBuch takes it and hints at KellerLog (F18; Martin, 05.11.2026: never refuse). */
export function isDrinkProduct(p: Pick<ProductInfo, "name" | "categories">): boolean {
  if (p.categories.some((t) => /^en:(beverages|alcoholic-beverages|waters|wines|beers|juices)/.test(t)) && !p.categories.some((t) => /^en:(dairies|milks|plant-milks|dairy-drinks)/.test(t))) return true;
  return false;
}

const ORGANIC = /^(en:organic|en:eu-organic|de:bio|en:bio|fr:ab-agriculture-biologique|en:demeter|en:bioland|en:naturland)$/;

/**
 * The fields of a new pantry article for a product (VorratsBuch's form, prefilled): profile,
 * Bezeichnung without the brand, brand, base product, aisle, unit and content of one pack, organic,
 * allergens, keeping; the code in `barcodes`. A household good (beauty and other products, or a
 * base product of a household aisle) gets the profile Haushalt.
 */
export function pantryFieldsFromProduct(p: ProductInfo): Record<string, unknown> {
  const base = baseOfProduct(p);
  const household = p.source !== "openfoodfacts" || (base !== undefined && HOUSEHOLD_AISLES.includes(base.aisle));
  const profile = household ? HOUSEHOLD_PROFILE : FOOD_PROFILE;
  const q = parseQuantity(p.quantity);
  const brand = p.brand?.split(",")[0]?.trim() || undefined;
  // „Mutti Polpa“ with the brand Mutti: the Bezeichnung is „Polpa“.
  let product = p.name.trim();
  if (brand && product.toLowerCase().startsWith(`${brand.toLowerCase()} `)) product = product.slice(brand.length + 1).trim();
  const unit = q?.unit ?? suggestedUnit(base);
  // A drink without a base product still goes under Getränke.
  const aisle = AISLES.find((a) => a.id === (base?.aisle ?? (isDrinkProduct(p) ? DRINKS_AISLE : undefined)))?.name;
  const allergens = p.allergens.filter((id) => ALLERGEN_NAMES[id] !== undefined);
  const fields: Record<string, unknown> = {
    profile: profile.id,
    product,
    ...(brand ? { brand } : {}),
    ...(base ? { base: base.id } : {}),
    ...(aisle ? { aisle, category: aisle } : {}),
    unit,
    // A content of 1 piece is no content: „1 Stück“ counts the piece itself.
    ...(q && !(q.unit === "Stk." && q.size === 1) ? { size: q.size } : {}),
    ...(!household && p.labels.some((l) => ORGANIC.test(l)) ? { organic: true } : {}),
    ...(!household && allergens.length ? { allergens } : {}),
    ...(!household && suggestedKeep(base) ? { keep: suggestedKeep(base) } : {}),
    ...(unit === "Stk." && suggestedCountBy(base) ? { countBy: suggestedCountBy(base) } : {}),
    tracking: profile.tracking,
    barcodes: [p.code],
  };
  return { ...fields, name: pantryName(fields) };
}

/** The base product named in fields, for showing („Passierte Tomaten“). */
export const baseNameOf = (fields: Record<string, unknown>) => baseProduct(fields.base)?.names[0];
